/**
 * Skills React Query Hooks
 * Data fetching and mutations for skills management
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { skillsApi } from "@/lib/api/skills";
import type { CreateSkillRequest, UpdateSkillRequest } from "@/types/skills";

/**
 * Hook to fetch all skills
 */
export function useSkills(includeDisabled = true) {
  return useQuery({
    queryKey: ["skills", includeDisabled],
    queryFn: () => skillsApi.list(includeDisabled),
  });
}

/**
 * Hook to fetch a single skill
 */
export function useSkill(skillId: string | null) {
  return useQuery({
    queryKey: ["skill", skillId],
    queryFn: () => skillsApi.get(skillId!),
    enabled: !!skillId,
  });
}

/**
 * Hook to create a new skill
 */
export function useCreateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSkillRequest) => skillsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

/**
 * Hook to update a skill
 */
export function useUpdateSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      skillId,
      data,
    }: {
      skillId: string;
      data: UpdateSkillRequest;
    }) => skillsApi.update(skillId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
      queryClient.invalidateQueries({ queryKey: ["skill", variables.skillId] });
    },
  });
}

/**
 * Hook to delete a skill
 */
export function useDeleteSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skillId: string) => skillsApi.delete(skillId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

/**
 * Hook to toggle skill enabled status
 * Requires both skillId and the new enabled state
 */
export function useToggleSkill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skillId, enabled }: { skillId: string; enabled: boolean }) =>
      skillsApi.toggle(skillId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}

/**
 * Hook to reorder skills
 */
export function useReorderSkills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      skillOrders: Array<{
        id: string;
        display_order: number;
        display_row: number;
      }>,
    ) => skillsApi.reorder(skillOrders),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["skills"] });
    },
  });
}
